import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import type { DocumentContent } from '@/types';

interface Props {
  content: DocumentContent;
  onChange: (content: DocumentContent) => void;
}

function EditableField({
  label,
  value,
  onChange,
  multiline = false,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
}) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full px-3 py-2 text-sm bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-y transition-colors"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
        />
      )}
    </div>
  );
}

export function DocumentEditPanel({ content, onChange }: Props) {
  const updateParties = (field: keyof DocumentContent['parties'], value: string) => {
    onChange({
      ...content,
      parties: { ...content.parties, [field]: value },
    });
  };

  const updateMetadata = (field: keyof DocumentContent['metadata'], value: string) => {
    onChange({
      ...content,
      metadata: { ...content.metadata, [field]: value },
    });
  };

  const updateSection = (sectionId: string, body: string) => {
    onChange({
      ...content,
      sections: content.sections.map((s) =>
        s.id === sectionId ? { ...s, body } : s
      ),
    });
  };

  const editableSections = content.sections.filter((s) => s.editable);
  const disclaimerSection = content.sections.find((s) => !s.editable);

  return (
    <div className="h-full overflow-y-auto p-5 space-y-6">
      {/* Parties */}
      <div>
        <h3 className="text-xs font-bold text-surface-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs">1</span>
          Parties
        </h3>
        <div className="bg-surface-50 rounded-xl p-4 space-y-1">
          <EditableField label="Your Name" value={content.parties.sender_name} onChange={(v) => updateParties('sender_name', v)} />
          <EditableField label="Your Address" value={content.parties.sender_address} onChange={(v) => updateParties('sender_address', v)} multiline rows={2} />
          <EditableField label="Recipient Name" value={content.parties.recipient_name} onChange={(v) => updateParties('recipient_name', v)} />
          <EditableField label="Recipient Address" value={content.parties.recipient_address} onChange={(v) => updateParties('recipient_address', v)} multiline rows={2} />
        </div>
      </div>

      {/* Details */}
      <div>
        <h3 className="text-xs font-bold text-surface-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs">2</span>
          Details
        </h3>
        <div className="bg-surface-50 rounded-xl p-4 space-y-1">
          <EditableField label="Date" value={content.metadata.date} onChange={(v) => updateMetadata('date', v)} />
          <EditableField label="Jurisdiction / State" value={content.metadata.jurisdiction} onChange={(v) => updateMetadata('jurisdiction', v)} />
          <EditableField label="Subject" value={content.metadata.subject} onChange={(v) => updateMetadata('subject', v)} />
        </div>
      </div>

      {/* Sections */}
      <div>
        <h3 className="text-xs font-bold text-surface-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs">3</span>
          Document Content
        </h3>
        <div className="space-y-4">
          {editableSections.map((section) => (
            <div key={section.id} className="bg-surface-50 rounded-xl p-4">
              <label className="block text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                {section.label}
              </label>
              <textarea
                value={section.body}
                onChange={(e) => updateSection(section.id, e.target.value)}
                rows={5}
                className="w-full px-3 py-2 text-sm bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-y transition-colors"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer (read-only) */}
      {disclaimerSection && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <NotLegalIcon name="info" className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">
              {disclaimerSection.label} (Required)
            </span>
          </div>
          <p className="text-xs text-amber-700 leading-relaxed">{disclaimerSection.body}</p>
        </div>
      )}
    </div>
  );
}
